import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { useNotification } from '@/components/notifications';
import { AuthContext } from '@/context/auth-context';
import { useOrgStore } from '@/stores/org-store';
import { useListTriggerConnections } from '@/hooks/queries/triggers/use-list-trigger-connections';
import { useListTriggers } from '@/hooks/queries/triggers/use-list-triggers';
import {
  useDeleteTrigger,
  useDeleteTriggerConnection,
} from '@/hooks/mutations/triggers/use-trigger-mutations';
import { Trigger, TriggerConnection } from '@/services/triggers/types';
import { ConnectionCard } from './providers/google-drive/connection-card';
import { useConnectGoogleDrive } from './use-connect-google-drive';

export const TriggersPage = () => {
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const { user } = useContext(AuthContext);
  const orgId = currentOrg?.orgid;
  const myEmail = user?.email;
  const [searchParams, setSearchParams] = useSearchParams();

  const [triggerToDelete, setTriggerToDelete] = useState<Trigger | null>(null);
  const [connectionToDelete, setConnectionToDelete] =
    useState<TriggerConnection | null>(null);

  const connectionsQuery = useListTriggerConnections(orgId);
  const triggersQuery = useListTriggers(orgId);
  const deleteConnection = useDeleteTriggerConnection(orgId);
  const deleteTrigger = useDeleteTrigger(orgId);
  // One instance for the page: the cards share its in-flight state so a second
  // consent cannot be started while one is still running.
  const { connect, reconnect, isConnecting } = useConnectGoogleDrive(
    orgId,
    myEmail,
  );

  const oauthStatus = searchParams.get('status');
  useEffect(() => {
    if (!oauthStatus) return;
    if (oauthStatus === 'success') {
      notification.success?.('Google Drive connected');
    } else {
      notification.error('Google Drive authorization did not complete');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('connection_id');
    setSearchParams(next, { replace: true });
  }, [oauthStatus, notification, searchParams, setSearchParams]);

  const managedConnections = useMemo(
    () =>
      (connectionsQuery.data ?? []).filter(
        (c) => c.provider === 'google_drive' && c.auth_backend === 'managed',
      ),
    [connectionsQuery.data],
  );

  // Each person connects their own Google account, so "mine" is the connection
  // created under my email — not simply the first one in the org.
  const myConnection = useMemo(
    () =>
      managedConnections.find(
        (c) => !!myEmail && c.config?.connected_by_email === myEmail,
      ),
    [managedConnections, myEmail],
  );

  const triggersByConnection = useMemo(() => {
    const map = new Map<string, Trigger[]>();
    (triggersQuery.data ?? []).forEach((t) => {
      map.set(t.connection_id, [...(map.get(t.connection_id) ?? []), t]);
    });
    return map;
  }, [triggersQuery.data]);

  // Triggers on a colleague's connection come back redacted and have no card to
  // sit under here. Counting them beats dropping them silently; the workflow's
  // own Triggers section is where they are actually actionable.
  const othersTriggerCount = useMemo(
    () => (triggersQuery.data ?? []).filter((t) => t.redacted).length,
    [triggersQuery.data],
  );

  const handleDeleteTrigger = async () => {
    if (!triggerToDelete) return;
    try {
      await deleteTrigger.mutateAsync(triggerToDelete.id);
      notification.success?.('Trigger deleted');
      setTriggerToDelete(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { triggerId: triggerToDelete.id },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not delete the trigger',
      );
    }
  };

  const handleDisconnect = async () => {
    if (!connectionToDelete) return;
    try {
      await deleteConnection.mutateAsync(connectionToDelete.id);
      notification.success?.('Google Drive disconnected');
      setConnectionToDelete(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { connectionId: connectionToDelete.id },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not disconnect',
      );
    }
  };

  const isLoading = connectionsQuery.isLoading || triggersQuery.isLoading;

  return (
    <div className='space-y-6 p-6'>
      <div>
        <PageTitle title='Triggers' />
        <PageDescription>
          Start workflows automatically when something happens somewhere else.
          Attach a trigger to as many workflows as you like.
        </PageDescription>
      </div>

      {isLoading ? (
        <Loader2 className='size-5 animate-spin' />
      ) : (
        <section className='space-y-4'>
          <div>
            <h2 className='text-sm font-semibold'>Google Drive</h2>
            <p className='text-xs text-muted-foreground'>
              Watch a folder in your Drive. When a file lands there, the
              workflows you attach run with that file as input.
            </p>
          </div>

          {!myConnection && (
            <div className='flex items-center justify-between gap-4 rounded-lg border p-4'>
              <div>
                <p className='font-medium'>Connect your Google Drive</p>
                <p className='text-xs text-muted-foreground'>
                  Capitol reads only the folders you choose. Everyone connects
                  their own account — nobody else in this organization can see
                  your Drive.
                </p>
              </div>
              <Button onClick={connect} disabled={isConnecting}>
                {isConnecting && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                Connect Google Drive
              </Button>
            </div>
          )}

          {managedConnections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              triggers={triggersByConnection.get(connection.id) ?? []}
              orgId={orgId}
              isMine={connection.id === myConnection?.id}
              onResumeConsent={reconnect}
              isConnecting={isConnecting}
              onRequestDisconnect={setConnectionToDelete}
              onRequestDeleteTrigger={setTriggerToDelete}
            />
          ))}

          {othersTriggerCount > 0 && (
            <p className='text-xs text-muted-foreground'>
              {othersTriggerCount} trigger{othersTriggerCount === 1 ? '' : 's'}{' '}
              on other members&apos; Google accounts also start workflows in
              this organization. Open a workflow&apos;s Triggers section to see
              the ones that affect it.
            </p>
          )}
        </section>
      )}

      <DeleteConfirmationModal
        isOpen={!!triggerToDelete}
        handleCancel={() => setTriggerToDelete(null)}
        handleDelete={handleDeleteTrigger}
        isLoading={deleteTrigger.isPending}
        buttonLabel='Delete trigger'
        title='Delete trigger'
        description='New uploads to this folder will stop starting workflows. Runs already in progress are unaffected.'
      />

      <DeleteConfirmationModal
        isOpen={!!connectionToDelete}
        handleCancel={() => setConnectionToDelete(null)}
        handleDelete={handleDisconnect}
        isLoading={deleteConnection.isPending}
        buttonLabel='Disconnect'
        title='Disconnect Google Drive'
        description={
          connectionToDelete?.config?.connected_by_email
            ? `This revokes Capitol's access to ${connectionToDelete.config.connected_by_email}'s Drive and deletes every watched folder on it.`
            : 'This revokes access and deletes every watched folder on this connection.'
        }
      />
    </div>
  );
};
